'use client'

import { useTranslations } from 'next-intl'
import { useMemo, useRef, useState } from 'react'
import { PaginationDots } from '@/components/pagination-dots'
import { PhoneScreen } from '@/components/phone-screen'
import { Section } from '@/components/section'
import { T } from '@/components/t'
import { Language } from '@/setup/locales'
import { debounce } from '@/utils/functions/debounce'
import { run } from '@/utils/run'

enum HomeHowToContentType {
    RESTAURANT = 'restaurant',
    MAP = 'map',
    DEAL = 'deal',
}

export function HomeHowTo() {
    const t = useTranslations()

    const items = useMemo(() => {
        return [
            {
                title: t('home.howTo.items.first.title'),
                description: t('home.howTo.items.first.description'),
                contentType: HomeHowToContentType.MAP,
            },
            {
                title: t('home.howTo.items.second.title'),
                description: t('home.howTo.items.second.description'),
                contentType: HomeHowToContentType.RESTAURANT,
            },
            {
                title: t('home.howTo.items.third.title'),
                description: t('home.howTo.items.third.description'),
                contentType: HomeHowToContentType.DEAL,
            },
        ]
    }, [t])

    const scrollContainerRef = useRef<HTMLDivElement>(null)

    const [currentImagePage, setCurrentImagePage] = useState(1)

    const onScroll = useMemo(() => {
        return debounce(() => {
            const container = scrollContainerRef.current

            if (!container) return

            const pageWidth = container.clientWidth * 0.7
            const currentPage = Math.round(container.scrollLeft / pageWidth) + 1

            if (currentPage !== currentImagePage) {
                setCurrentImagePage(currentPage)
            }
        }, 100)
    }, [currentImagePage])

    return (
        <Section
            bleedClassName="bg-[#F8FBFD] py-[72px]"
            className="flex flex-col items-center px-0 md:px-8"
        >
            <div className="mb-4">
                <div className="flex flex-col items-center mb-8">
                    <T
                        div
                        t="home.howTo.label"
                        className="text-[rgb(66,186,150)] font-bold px-3 py-1 text-[12px] bg-[rgba(66,186,150,0.1)] w-fit rounded-full
                            mb-1"
                    />

                    <T h2 t="home.howTo.title" className="text-[36px] md:text-[40px]" />
                </div>
            </div>

            <div
                onScroll={onScroll}
                ref={scrollContainerRef}
                className="flex md:flex-row items-start md:justify-around px-2 gap-x-1 md:gap-x-0 md:px-0 overflow-x-auto
                    w-full max-w-full md:snap-none snap-x snap-mandatory scrollbar-hide"
            >
                {/* Invisible spacer to center the first item */}
                {/* <div className="flex-shrink-0 w-[0%] md:hidden"></div> */}

                {items.map((item, index) => {
                    return (
                        <div
                            key={item.contentType + 'client-home-how-to' + index}
                            className="relative z-[0] flex flex-col items-center min-w-[310px] max-w-[310px] flex-shrink-0 snap-center
                                snap-always md:snap-none"
                        >
                            {run(() => {
                                const getContentSrc = (language: Language) =>
                                    item.contentType === HomeHowToContentType.RESTAURANT
                                        ? `/_images/screens/restaurant-${language}.webp`
                                        : item.contentType === HomeHowToContentType.MAP
                                          ? `/_images/screens/map-${language}.webp`
                                          : `/_images/screens/redeem-${language}.webp`

                                return (
                                    <PhoneScreen
                                        className="w-[70%]"
                                        getContentSrc={getContentSrc}
                                    />
                                )
                            })}

                            <div className="flex flex-row relative mt-[48px] ml-[20px]">
                                <div
                                    className="bg-neotaste w-fit h-fit rounded-full min-w-[24px] min-h-[24px] flex items-center justify-center
                                        text-[12px] font-bold mr-2"
                                >
                                    {index + 1}
                                </div>

                                <div className="flex flex-col max-w-[260px] pt-[2px]">
                                    <h3 className="mb-2">{item.title}</h3>
                                    <p className="text-gray-500">{item.description}</p>
                                </div>
                            </div>
                        </div>
                    )
                })}

                {/* Invisible spacer to center the last item */}
                <div className="flex-shrink-0 w-[20%] md:hidden" />
            </div>

            <PaginationDots
                currentPage={currentImagePage}
                pagesCount={3}
                className="md:hidden mt-10"
                activeColorClassName="bg-black scale-2"
                inactiveColorClassName="bg-black/50"
            />
        </Section>
    )
}
